import React, { useEffect, useState } from 'react';
import './MarketplaceBuyerCommunicationsTab.scss';
import { TimelineParty } from '../../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { Timeline } from '../../../../../../slices/marketplace/timeline/models/timeline.model';
import { Button } from 'reactstrap';
import ReactSwitch from 'react-switch';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';
import { CommsHubBuyerSelectionModal } from './CommsHubBuyerSelectionModal/CommsHubBuyerSelectionModal';
import ClubhouseArea from '../../../../../../clubhouse/clubhouse-area/clubhouse-area';
import { MessageReference } from '../../../../../../clubhouse/models/message-reference';
import { GroupType } from '../../../../../../clubhouse/models/group-type';
import { useStateSelector } from '../../../../../../store/selectors';
import { USER_ROLES } from '../../../../../../utils/constants';
import { useSearchParams } from 'react-router-dom';
import { QueryParams } from '../../../../../../utils/queryParams';
import { MarketplaceOpportunity } from '../../../../../../slices/marketplace/opportunity/models/opportunity';
import { LevelOfService, isSfas } from '../../../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { resetClubhouse } from '../../../../../../clubhouse/slices/clubhouse/clubhouse-slice';
import { useDispatch } from 'react-redux';
import ConfirmationModal from '../../../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../../../utils/createNotification';
import { NotificationFlagsCategories } from '../../../../../../models/NotificationFlags/NotificationFlagsCategories';
import { EngagementProcessStatus } from '../../../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { MarketplaceOpportunityStatus } from '../../../../../../slices/marketplace/opportunity/models/opportunity-status';
import { useSignalR } from '../../../../../../context/WebsocketProvider';

interface MarketplaceBuyerCommunicationsTabProps {
    party: TimelineParty;
    timeline: Timeline;
    notificationFlagPrefix: string;
    isReadOnly?: boolean;
}

export interface BuyerCommunicationChat {
    id: number;
    name: string;
    clubhouseId?: number;
}
export const MarketplaceBuyerCommunicationsTab = (
    props: MarketplaceBuyerCommunicationsTabProps
) => {
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const axios = useStateSelector((s) => s.core.axios);
    const summary = useStateSelector((s) => s.userSummary.summary);
    const isSecondaryUser = summary?.roleId === USER_ROLES.PRACTICE_SECONDARY_USER;
    const isActingPrimaryBuyer = isSecondaryUser &&
        (summary?.associateMember?.[0]?.associateMemberMarketplaceMappings ?? [])
            .some((m: any) => m.isGeneralSettings && m.isActingPrimaryBuyer);
    const dispatch = useDispatch();
    const [availableChats, setAvailableChats] = useState<
        BuyerCommunicationChat[]
    >([]);
    const [selectedChat, setSelectedChat] =
        useState<BuyerCommunicationChat | null>(null);
    const [isBuyerSelectionModalOpened, setIsBuyerSelectionModalOpened] =
        useState(false);
    const [messageReference, setMessageReference] = useState<
        MessageReference | undefined
    >();
    // Starts true so the chat shows a loader (not a blank pane) until the
    // clubhouse fetch settles; ClubhouseArea flips it off.
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [isForbiddenError, setIsForbiddenError] = useState(false);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const [searchParams] = useSearchParams();
    const [timelineId, setTimelineId] = useState<number | null>(null);
    const isUserAdmin =
        useStateSelector((s) => s.currentUser.user.roleId) === USER_ROLES.ADMIN;
    const [isBrokerReviewEnabled, setIsBrokerReviewEnabled] = useState(
        props.timeline?.engagementProcess?.isBrokerReviewEnabled
    );
    const [isBuyerChatsEnabled, setIsBuyerChatsEnabled] = useState(
        props.timeline?.engagementProcess?.isBuyerChatsEnabled
    );
    const [
        isBrokerReviewToggleConfirmationShown,
        setIsBrokerReviewToggleConfirmationShown,
    ] = useState(false);
    const [
        isBuyerChatsEnableToggleConfirmationShown,
        setIsBuyerChatsEnableToggleConfirmationShown,
    ] = useState(false);
    const [isBrokerReviewLoading, setIsBrokerReviewLoading] = useState(false);
    const [
        isBuyerChatsStatusChangeLoading,
        setIsBuyerChatsStatusChangeLoading,
    ] = useState(false);
    const [isTimelineActive, setIsTimelineActive] = useState(false);

    useEffect(() => {
        setIsBrokerReviewEnabled(
            currentEngagement?.engagementProcess?.isBrokerReviewEnabled
        );
    }, [currentEngagement]);

    const { isConnected } = useSignalR();

    // If the chat failed to load while the connection was down, retry as soon
    // as the connection returns instead of parking on the error message.
    useEffect(() => {
        if (isConnected && isUnexpectedError) {
            setIsUnexpectedError(false);
            setIsDataLoading(true);
        }
    }, [isConnected]);

    const sellerBrokerHeader = () => {
        return (
            <div>
                <div className="d-flex justify-content-between flex-wrap">
                    <div className="chat-header-row">
                        <strong>{selectedChat?.name}</strong>
                        {availableChats.length > 1 && (
                            <Button
                                className="btn btn-primary ml-2 btn-switch-buyer-sm"
                                onClick={() =>
                                    setIsBuyerSelectionModalOpened(true)
                                }>
                                Switch Buyer
                            </Button>
                        )}
                    </div>
                    {props.party === TimelineParty.Seller &&
                    props.timeline.levelOfService ===
                        LevelOfService.FullServiceBrokerage &&
                    props.timeline.engagementProcess.broker?.isActive ? (
                        <div className="d-flex align-items-center">
                            <ReactSwitch
                                disabled={
                                    props.isReadOnly ||
                                    props.timeline.engagementProcess.status !==
                                    EngagementProcessStatus.Current
                                }
                                className="mr-2"
                                offColor="#d92550"
                                checked={isBrokerReviewEnabled}
                                onChange={(event) => {
                                    setIsBrokerReviewToggleConfirmationShown(
                                        true
                                    );
                                }}
                                height={22}
                                handleDiameter={16}></ReactSwitch>
                            Advisor Review{' '}
                            <InfoTooltip text="By enabling advisor review, messages will not be posted until they have been reviewed and approved by your advisor." />
                        </div>
                    ) : (
                        []
                    )}
                    {props.party === TimelineParty.VetValue &&
                    isSfas(props.timeline.levelOfService) ? (
                        <div className="d-flex align-items-center">
                            <div className="mr-2">Deactivate</div>
                            <ReactSwitch
                                className="mr-2"
                                offColor="#d92550"
                                checked={isBuyerChatsEnabled}
                                onChange={(event) => {
                                    setIsBuyerChatsEnableToggleConfirmationShown(
                                        true
                                    );
                                }}
                                height={22}
                                handleDiameter={16}></ReactSwitch>
                            <div>Activate</div>
                        </div>
                    ) : (
                        []
                    )}
                </div>
            </div>
        );
    };

    const getHeader = () => {
        return props.party === TimelineParty.Buyer ? (
            <></>
        ) : (
            sellerBrokerHeader()
        );
    };

    useEffect(() => {
        let chats = [];
        if (props.party === TimelineParty.Buyer) {
            const opp = props.timeline as unknown as MarketplaceOpportunity;
            chats.push({
                id: 1,
                name: 'Seller Communications',
                clubhouseId: opp.buyer.buyerCommsId,
            });
            setTimelineId(opp.opportunityId);
            setIsTimelineActive(
                opp.status !== MarketplaceOpportunityStatus.Suspended &&
                    opp.status !== MarketplaceOpportunityStatus.Terminated
            );
        } else {
            props.timeline.engagementProcess.buyers
                .filter((b) => b.isDataNdaSigned && b.isActive)
                .map((b) => {
                    chats.push({
                        id: b.buyerId,
                        name: b.companyName,
                        clubhouseId: b.buyerCommsId,
                    });
                });
            setTimelineId(props.timeline.id);
            setIsTimelineActive(
                props.timeline.engagementProcess.status ===
                    EngagementProcessStatus.Current
            );
        }
        setAvailableChats(chats);
        
        const commsIdFromQuery = searchParams.get(QueryParams.CommsIdKey);
        if (commsIdFromQuery) {
            const matchingChat = chats.find(
                (chat) => chat.clubhouseId === Number(commsIdFromQuery)
            );
            if (matchingChat) {
                if (selectedChat?.clubhouseId !== matchingChat.clubhouseId) {
                    dispatch(resetClubhouse());
                }
                setSelectedChat(matchingChat);
            } else {
                setSelectedChat(chats.length > 0 ? chats[0] : null);
            }
        } else {
            setSelectedChat(chats.length > 0 ? chats[0] : null);
        }
    }, [searchParams]);

    const updateBrokerReviewStatus = (enabled: boolean) => {
        setIsBrokerReviewLoading(true);
        axios
            .put(
                `api/Marketplace/Engagements/${props.timeline.id}/brokerReview/${enabled}`
            )
            .then((response) => {
                if (response.status === 200) {
                    setIsBrokerReviewEnabled(enabled);
                    setIsBrokerReviewToggleConfirmationShown(false);
                    createNotification(
                        `Advisor Review has been successfully ${
                            enabled ? 'enabled' : 'disabled'
                        }`,
                        'success'
                    );
                } else {
                    createNotification(
                        'An error occurred during the Advisor Review status update. Please try again later.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An error occurred during the Advisor Review status update. Please try again later.',
                    'error'
                );
            })
            .finally(() => {
                setIsBrokerReviewLoading(false);
            });
    };

    const updateBuyerChatsStatus = (enabled: boolean) => {
        setIsBuyerChatsStatusChangeLoading(true);
        axios
            .put(
                `api/Marketplace/Engagements/${props.timeline.id}/buyerChatsStatus/${enabled}`
            )
            .then((response) => {
                if (response.status === 200) {
                    setIsBuyerChatsEnabled(enabled);
                    setIsBuyerChatsEnableToggleConfirmationShown(false);
                    createNotification(
                        `Buyer Chats has been successfully ${
                            enabled ? 'enabled' : 'disabled'
                        }`,
                        'success'
                    );
                } else {
                    createNotification(
                        'An error occurred during the Buyer Chats status update. Please try again later.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An error occurred during the Buyer Chats status update. Please try again later.',
                    'error'
                );
            })
            .finally(() => {
                setIsBuyerChatsStatusChangeLoading(false);
            });
    };

    return (
        <div className="marketplace-buyer-communications-tab marketplace-dynamic-container">
            <div className="body_inner_content marketplace-dynamic-container">
                <div className="sampledetail">{getHeader()}</div>

                <div className="clubhouse-dynamic-container">
                    <div className="app-pmg-clubhouse app-clubhouse-page">
                        {isForbiddenError ? (
                            <h2 className="error-message">
                                You're not authorized to view this page
                            </h2>
                        ) : isUnexpectedError ? (
                            <h2 className="error-message">
                                Unexpected error occured while fetching Comms
                                Hub
                            </h2>
                        ) : selectedChat ? (
                            <div className="flex-container">
                                <ClubhouseArea
                                    messageReference={messageReference}
                                    resetMessageReference={() => {
                                        setMessageReference(undefined);
                                    }}
                                    clubhouseId={selectedChat.clubhouseId}
                                    groupType={GroupType.MP}
                                    isAdmin={isUserAdmin}
                                    isDataLoading={isDataLoading}
                                    setIsDataLoading={setIsDataLoading}
                                    isActive={isActive}
                                    setIsActive={setIsActive}
                                    setIsForbiddenError={setIsForbiddenError}
                                    refreshClubhouseData={() => {}}
                                    setIsUnexpectedError={setIsUnexpectedError}
                                    isDisabledMessageInput={props.isReadOnly || !isTimelineActive}
                                    isDisabledLikeButton={props.isReadOnly || !isTimelineActive}
                                    isKbAvailable={false}
                                    preselectedDiscussionId={Number(
                                        searchParams.get(
                                            QueryParams.DiscussionIdKey
                                        )
                                    )}
                                    displayTopLikedPosts={() => {}}
                                    customDiscussionsName="Topic"
                                    isDisscussionDeleteIconVisible={
                                        !props.isReadOnly &&
                                        (!isSecondaryUser || isActingPrimaryBuyer) &&
                                        (props.party !== TimelineParty.Broker ||
                                            isSfas(props.timeline.levelOfService))
                                    }
                                    customNfPrefix={`${props.notificationFlagPrefix}.${timelineId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.BuyerCommsCode}.${selectedChat.clubhouseId}`}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
            {isBuyerSelectionModalOpened ? (
                <CommsHubBuyerSelectionModal
                    onClose={() => setIsBuyerSelectionModalOpened(false)}
                    isVisible={isBuyerSelectionModalOpened}
                    engagement={props.timeline}
                    selectedChat={selectedChat}
                    availableChats={availableChats}
                    onChatSelect={(chat) => {
                        setSelectedChat(chat);
                        if (selectedChat.clubhouseId !== chat.clubhouseId) {
                            dispatch(resetClubhouse());
                        }
                        setIsBuyerSelectionModalOpened(false);
                    }}
                />
            ) : (
                []
            )}
            {isBrokerReviewToggleConfirmationShown ? (
                <ConfirmationModal
                    isVisible={isBrokerReviewToggleConfirmationShown}
                    setIsVisible={setIsBrokerReviewToggleConfirmationShown}
                    header={'Confirm Advisor Review Status Change'}
                    confirmationText={`Are you sure you want to ${
                        isBrokerReviewEnabled ? 'Disable' : 'Enable'
                    } the Advisor review?`}
                    onConfirm={() => {
                        updateBrokerReviewStatus(!isBrokerReviewEnabled);
                    }}
                    params={undefined}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isBrokerReviewLoading}></ConfirmationModal>
            ) : (
                []
            )}
            {isBuyerChatsEnableToggleConfirmationShown ? (
                <ConfirmationModal
                    isVisible={isBuyerChatsEnableToggleConfirmationShown}
                    setIsVisible={setIsBuyerChatsEnableToggleConfirmationShown}
                    header={`Confirm ${
                        isBuyerChatsEnabled ? 'the disabling' : 'the enabling'
                    } of Buyer Chats`}
                    confirmationText={`Are you sure you want to ${
                        isBuyerChatsEnabled ? 'Disable' : 'Enable'
                    } Buyer Chats?`}
                    onConfirm={() => {
                        updateBuyerChatsStatus(!isBuyerChatsEnabled);
                    }}
                    params={undefined}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={
                        isBuyerChatsStatusChangeLoading
                    }></ConfirmationModal>
            ) : (
                []
            )}
        </div>
    );
};
